
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import {
  RefreshCw, CreditCard, CheckCircle2, XCircle, AlertCircle, FileSpreadsheet, 
  Download, Upload, Eye, ArrowDownRight, ArrowUpRight
} from 'lucide-react';
import {
  Tabs, TabsList, TabsTrigger, TabsContent
} from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  Card, CardContent, CardHeader, CardTitle
} from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'Cheque', 'UPI', 'NEFT', 'RTGS', 'IMPS'];

export default function FinancePayments() {
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [loading, setLoading] = useState({
    advances: false,
    reimbursements: false,
    refunds: false,
    recoveries: false,
    completed: false,
    bulkUpload: false,
    downloadTemplate: false
  });
  const [data, setData] = useState({
    advances: [],
    reimbursements: [],
    refunds: [],
    recoveries: [],
    completed: []
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: '',
    bank: '',
    transactionRef: '',
    utrNumber: '',
    chequeNumber: '',
    remarks: ''
  });
  // Bulk upload state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch { toast.error('Failed to load companies'); }
    })();
  }, []);

  const loadPendingAdvances = async () => {
    if (!selCompany) return;
    setLoading(prev => ({ ...prev, advances: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/pending-advances?company_id=${selCompany}`, { withCredentials: true });
      setData(prev => ({ ...prev, advances: res.data || [] }));
    } catch {
      toast.error('Failed to load pending advances');
    } finally {
      setLoading(prev => ({ ...prev, advances: false }));
    }
  };

  const loadPendingReimbursements = async () => {
    if (!selCompany) return;
    setLoading(prev => ({ ...prev, reimbursements: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/pending-reimbursements?company_id=${selCompany}`, { withCredentials: true });
      setData(prev => ({ ...prev, reimbursements: res.data || [] }));
    } catch {
      toast.error('Failed to load pending reimbursements');
    } finally {
      setLoading(prev => ({ ...prev, reimbursements: false }));
    }
  };

  const loadEmployeeRefunds = async () => {
    if (!selCompany) return;
    setLoading(prev => ({ ...prev, refunds: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/employee-refunds?company_id=${selCompany}`, { withCredentials: true });
      setData(prev => ({ ...prev, refunds: res.data || [] }));
    } catch {
      toast.error('Failed to load employee refunds');
    } finally {
      setLoading(prev => ({ ...prev, refunds: false }));
    }
  };

  const loadRecoveryCollection = async () => {
    if (!selCompany) return;
    setLoading(prev => ({ ...prev, recoveries: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/recovery-collection?company_id=${selCompany}`, { withCredentials: true });
      setData(prev => ({ ...prev, recoveries: res.data || [] }));
    } catch {
      toast.error('Failed to load recovery collection');
    } finally {
      setLoading(prev => ({ ...prev, recoveries: false }));
    }
  };

  const loadCompletedPayments = async () => {
    if (!selCompany) return;
    setLoading(prev => ({ ...prev, completed: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/completed-payments?company_id=${selCompany}`, { withCredentials: true });
      setData(prev => ({ ...prev, completed: res.data || [] }));
    } catch {
      toast.error('Failed to load completed payments');
    } finally {
      setLoading(prev => ({ ...prev, completed: false }));
    }
  };

  useEffect(() => {
    if (!selCompany) return;
    loadPendingAdvances();
    loadPendingReimbursements();
    loadEmployeeRefunds();
    loadRecoveryCollection();
    loadCompletedPayments();
  }, [selCompany]);

  const confirmPayment = async () => {
    if (!confirmDialog) return;
    try {
      const payload = {
        ...confirmDialog,
        company_id: selCompany,
        ...paymentDetails,
        amount: Number(paymentDetails.amount)
      };
      await axios.post(`${API_URL}/finance-payments/confirm`, payload, { withCredentials: true });
      toast.success('Payment confirmed successfully!');
      setConfirmDialog(null);
      // Reload relevant data
      if (confirmDialog.type === 'advance_payment') loadPendingAdvances();
      else if (confirmDialog.type === 'reimbursement_payment') loadPendingReimbursements();
      loadCompletedPayments();
    } catch {
      toast.error('Failed to confirm payment');
    }
  };

  const openConfirmDialog = (type, item) => {
    let amount = 0;
    if (type === 'advance_payment') {
      amount = item.pending_amount;
    } else if (type === 'reimbursement_payment') {
      amount = item.pending_amount;
    } else if (type === 'employee_refund') {
      amount = Math.abs(item.pending_balance);
    } else if (type === 'recovery_collection') {
      amount = item.pending_balance;
    }
    setPaymentDetails({
      amount: amount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: '',
      bank: '',
      transactionRef: '',
      utrNumber: '',
      chequeNumber: '',
      remarks: ''
    });
    setConfirmDialog({
      type,
      item,
      related_id: type === 'advance_payment' ? item.id :
        type === 'reimbursement_payment' ? item.id :
        type === 'employee_refund' ? item.id : item.id
    });
  };
  
  // Download template function
  const handleDownloadTemplate = async () => {
    if (!selCompany) {
      toast.error('Please select a company first');
      return;
    }
    setLoading(prev => ({ ...prev, downloadTemplate: true }));
    try {
      const res = await axios.get(`${API_URL}/finance-payments/download-template`, {
        params: { company_id: selCompany },
        responseType: 'blob',
        withCredentials: true,
      });
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
        || `Finance_Payments_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
      console.error(e);
    } finally {
      setLoading(prev => ({ ...prev, downloadTemplate: false }));
    }
  };
  
  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!selCompany || !bulkFile) {
      toast.error('Please select a company and file');
      return;
    }
    setLoading(prev => ({ ...prev, bulkUpload: true }));
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      
      const res = await axios.post(
        `${API_URL}/finance-payments/bulk-upload?company_id=${selCompany}`,
        formData,
        { withCredentials: true }
      );
      setBulkResult(res.data);
      toast.success(`Bulk upload complete! ${res.data.success_count} payments processed`);
      // Reload data
      loadPendingAdvances();
      loadPendingReimbursements();
      loadCompletedPayments();
    } catch (e) {
      toast.error('Failed to process upload');
      console.error(e);
    } finally {
      setLoading(prev => ({ ...prev, bulkUpload: false }));
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Finance Payments</h1>
            <p className="text-slate-500 mt-1">Confirm and track all finance transactions</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Company:</span>
              <select
                className="border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={selCompany}
                onChange={(e) => setSelCompany(e.target.value)}
              >
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                loadPendingAdvances();
                loadPendingReimbursements();
                loadEmployeeRefunds();
                loadRecoveryCollection();
                loadCompletedPayments();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => { setBulkDialogOpen(true); setBulkFile(null); setBulkResult(null); }}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
          </div>
        </div>
        
        {/* Bulk Upload Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bulk Upload Finance Payments</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Step 1: Download Template */}
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <h3 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Download className="w-4 h-4" /> Step 1: Download Template
                </h3>
                <p className="text-sm text-slate-500 mb-3">Download pre-filled template with pending advances/reimbursements</p>
                <Button onClick={handleDownloadTemplate} disabled={loading.downloadTemplate}>
                  {loading.downloadTemplate ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download Template
                </Button>
              </div>
              
              {/* Step 2: Upload File */}
              <div className="p-4 border border-slate-200 rounded-lg">
                <h3 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Step 2: Upload File
                </h3>
                <div className="space-y-3">
                  <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                  {bulkFile && <p className="text-sm text-slate-600">Selected file: {bulkFile.name}</p>}
                </div>
              </div>
              
              {/* Step 3: Preview / Results */}
              {bulkResult && (
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h3 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Upload Results
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="text-center p-2 border rounded">
                      <div className="text-2xl font-bold text-slate-700">{bulkResult.total_rows}</div>
                      <div className="text-xs text-slate-500">Total Rows</div>
                    </div>
                    <div className="text-center p-2 border rounded">
                      <div className="text-2xl font-bold text-green-600">{bulkResult.success_count}</div>
                      <div className="text-xs text-slate-500">Success</div>
                    </div>
                    <div className="text-center p-2 border rounded">
                      <div className="text-2xl font-bold text-orange-500">{bulkResult.invalid_count}</div>
                      <div className="text-xs text-slate-500">Invalid</div>
                    </div>
                    <div className="text-center p-2 border rounded">
                      <div className="text-2xl font-bold text-red-500">{bulkResult.failed_count}</div>
                      <div className="text-xs text-slate-500">Failed</div>
                    </div>
                  </div>
                  
                  {bulkResult.invalid_rows.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-orange-600 mb-2">Invalid Rows:</h4>
                      <div className="max-h-40 overflow-auto border rounded">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Row</TableHead>
                              <TableHead>Reference</TableHead>
                              <TableHead>Errors</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkResult.invalid_rows.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.row}</TableCell>
                                <TableCell>{row.reference_no || '-'}</TableCell>
                                <TableCell className="text-red-600">{row.errors.join(', ')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  {bulkResult.failed_rows.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-red-600 mb-2">Failed Rows:</h4>
                      <div className="max-h-40 overflow-auto border rounded">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Reference</TableHead>
                              <TableHead>Error</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkResult.failed_rows.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.reference_no || '-'}</TableCell>
                                <TableCell className="text-red-600">{row.error}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Close</Button>
              {!bulkResult && (
                <Button onClick={handleBulkUpload} disabled={!bulkFile || loading.bulkUpload}>
                  {loading.bulkUpload ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Process File
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="advances" className="w-full">
          <TabsList className="grid grid-cols-5 mb-6">
            <TabsTrigger value="advances">
              <CreditCard className="w-4 h-4 mr-2" />
              Pending Advances
            </TabsTrigger>
            <TabsTrigger value="reimbursements">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Pending Reimbursements
            </TabsTrigger>
            <TabsTrigger value="refunds">
              <ArrowDownRight className="w-4 h-4 mr-2" />
              Employee Refunds
            </TabsTrigger>
            <TabsTrigger value="recoveries">
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Recovery Collection
            </TabsTrigger>
            <TabsTrigger value="completed">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Completed Payments
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Pending Advances */}
          <TabsContent value="advances">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Pending Advance Payments</CardTitle>
              </CardHeader>
              <CardContent>
                {loading.advances ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference No.</TableHead>
                        <TableHead>Employee Code</TableHead>
                        <TableHead>Employee Name</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Advance Purpose</TableHead>
                        <TableHead>Approved Amount</TableHead>
                        <TableHead>Already Paid</TableHead>
                        <TableHead>Pending Amount</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.advances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                            No pending advance payments
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.advances.map((adv, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{adv.finance_ref || adv.loan_number || '-'}</TableCell>
                            <TableCell>{adv.employee_code || '-'}</TableCell>
                            <TableCell>{adv.employee_name || '-'}</TableCell>
                            <TableCell>{adv.department || '-'}</TableCell>
                            <TableCell>{adv.purpose || '-'}</TableCell>
                            <TableCell>{fmtINR(adv.advance_amount)}</TableCell>
                            <TableCell>{fmtINR(adv.paid_amount)}</TableCell>
                            <TableCell className="text-amber-600 font-medium">{fmtINR(adv.pending_amount)}</TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => openConfirmDialog('advance_payment', adv)}>
                                Confirm Payment
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Pending Reimbursements */}
          <TabsContent value="reimbursements">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Pending Reimbursement Payments</CardTitle>
              </CardHeader>
              <CardContent>
                {loading.reimbursements ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference No.</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Paid Amount</TableHead>
                        <TableHead>Pending Amount</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.reimbursements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No pending reimbursement payments
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.reimbursements.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.finance_ref}</TableCell>
                            <TableCell>{fmtINR(item.total_amount)}</TableCell>
                            <TableCell>{fmtINR(item.paid_amount)}</TableCell>
                            <TableCell className="text-amber-600 font-medium">{fmtINR(item.pending_amount)}</TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => openConfirmDialog('reimbursement_payment', item)}>
                                Confirm Payment
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Employee Refunds */}
          <TabsContent value="refunds">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Employee Refunds</CardTitle>
              </CardHeader>
              <CardContent>
                {loading.refunds ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee Code</TableHead>
                        <TableHead>Employee Name</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.refunds.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                            No employee refunds
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.refunds.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.employee_code}</TableCell>
                            <TableCell>{item.employee_name}</TableCell>
                            <TableCell className="text-green-600 font-medium">{fmtINR(Math.abs(item.pending_balance))}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="default" onClick={() => openConfirmDialog('employee_refund', item)}>
                                Record Refund
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab4: Recovery Collection */}
          <TabsContent value="recoveries">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Recovery Collection</CardTitle>
              </CardHeader>
              <CardContent>
                {loading.recoveries ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee Code</TableHead>
                        <TableHead>Employee Name</TableHead>
                        <TableHead>Pending Amount</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recoveries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                            No recovery collection pending
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.recoveries.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.employee_code}</TableCell>
                            <TableCell>{item.employee_name}</TableCell>
                            <TableCell className="text-red-600 font-medium">{fmtINR(item.pending_balance)}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="default" onClick={() => openConfirmDialog('recovery_collection', item)}>
                                Record Recovery
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab5: Completed Payments */}
          <TabsContent value="completed">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Completed Payments</CardTitle>
              </CardHeader>
              <CardContent>
                {loading.completed ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Mode</TableHead>
                        <TableHead>UTR/Cheque</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.completed.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No completed payments
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.completed.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="capitalize">{item.type.replace('_', ' ')}</TableCell>
                            <TableCell>{fmtINR(item.amount)}</TableCell>
                            <TableCell>{formatDate(item.payment_date)}</TableCell>
                            <TableCell>{item.payment_mode}</TableCell>
                            <TableCell>{item.utr_number || item.cheque_number || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Confirm Payment Dialog */}
        <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Confirm Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={paymentDetails.amount}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentDetails.paymentDate}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, paymentDate: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={paymentDetails.paymentMode} onValueChange={(v) => setPaymentDetails(prev => ({ ...prev, paymentMode: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map(mode => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bank (Optional)</Label>
                <Input
                  value={paymentDetails.bank}
                  onChange={(e) => setPaymentDetails(prev => ({ ...prev, bank: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transaction Ref</Label>
                  <Input
                    value={paymentDetails.transactionRef}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, transactionRef: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>UTR/Cheque No.</Label>
                  <Input
                    value={paymentDetails.utrNumber || paymentDetails.chequeNumber}
                    onChange={(e) => setPaymentDetails(prev => ({
                      ...prev,
                      utrNumber: e.target.value,
                      chequeNumber: e.target.value
                    }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={paymentDetails.remarks}
                  onChange={(e) => setPaymentDetails(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
              <Button onClick={confirmPayment}>Confirm Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
